import { useState, useEffect, useMemo } from 'react';
import Layout from '@/components/feature/Layout';
import { Venta } from '@/lib/supabase';
import { cargarTodosLosVentas, cargarMapaUsuarios, cargarDetalleVenta } from '@/lib/ventas';
import { generarPdfVenta, generarPdfHistorial, numeroVenta } from '@/lib/pdf';
import { calcularRango, PeriodoCPP } from '@/lib/cpp';
import DetalleVentaModal from './components/DetalleVentaModal';

const PAGE_SIZE = 12;

type TipoFiltro = 'todas' | 'hoy' | 'semana' | 'mes';

interface Notificacion {
  tipo: 'success' | 'error';
  mensaje: string;
}

const formatearBs = (valor: number): string =>
  `Bs. ${(Number.isFinite(valor) ? valor : 0).toLocaleString('es-BO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

export default function HistorialVentasPage() {
  const [ventas, setVentas] = useState<Venta[]>([]);
  const [empleados, setEmpleados] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [filtroFecha, setFiltroFecha] = useState<TipoFiltro>('todas');
  const [filtroPago, setFiltroPago] = useState('');
  const [pagina, setPagina] = useState(1);

  const [detalle, setDetalle] = useState<Venta | null>(null);
  const [exportandoId, setExportandoId] = useState<string | null>(null);
  const [exportandoGlobal, setExportandoGlobal] = useState(false);
  const [notificacion, setNotificacion] = useState<Notificacion | null>(null);

  const avisar = (tipo: Notificacion['tipo'], mensaje: string) => {
    setNotificacion({ tipo, mensaje });
    setTimeout(() => setNotificacion(null), 4000);
  };

  const cargar = async () => {
    setLoading(true);
    setError('');
    try {
      const [dataVentas, mapa] = await Promise.all([cargarTodosLosVentas(), cargarMapaUsuarios()]);
      setVentas(dataVentas);
      setEmpleados(mapa);
    } catch {
      setError('No se pudo cargar el historial de ventas. Revisá la conexión e intentá de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargar();
  }, []);

  useEffect(() => {
    setPagina(1);
  }, [search, filtroFecha, filtroPago]);

  const rango = useMemo(() => {
    if (filtroFecha === 'todas') return null;
    return calcularRango(filtroFecha as PeriodoCPP, new Date());
  }, [filtroFecha]);

  const periodoLabel = rango ? rango.label : 'Todas las ventas';

  const filtered = useMemo(() => {
    const desdeMs = rango ? rango.desde.getTime() : null;
    const hastaMs = rango ? rango.hasta.getTime() : null;
    const termino = search.trim().toLowerCase();

    return ventas.filter((v) => {
      const t = new Date(v.fecha).getTime();
      const coincideFecha =
        desdeMs === null || hastaMs === null ? true : t >= desdeMs && t <= hastaMs;

      const coincideBusqueda =
        termino === '' ||
        (v.nombre_factura || '').toLowerCase().includes(termino) ||
        (v.nit || '').toLowerCase().includes(termino) ||
        numeroVenta(v.id).toLowerCase().includes(termino);

      const coincidePago = filtroPago === '' || v.metodo_pago === filtroPago;

      return coincideFecha && coincideBusqueda && coincidePago;
    });
  }, [ventas, rango, search, filtroPago]);

  const resumen = useMemo(() => {
    const cantidad = filtered.length;
    const ingresos = filtered.reduce((s, v) => s + Number(v.total || 0), 0);
    const recibido = filtered.reduce((s, v) => s + Number(v.monto_recibido || 0), 0);
    const cambios = filtered.reduce((s, v) => s + Number(v.cambio || 0), 0);
    return { cantidad, ingresos, recibido, cambios };
  }, [filtered]);

  const totalPaginas = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginaActual = Math.min(pagina, totalPaginas);
  const pageItems = filtered.slice((paginaActual - 1) * PAGE_SIZE, paginaActual * PAGE_SIZE);

  const handleDescargarVenta = async (venta: Venta) => {
    setExportandoId(venta.id);
    try {
      const items = await cargarDetalleVenta(venta.id);
      await generarPdfVenta(venta, items, empleados[venta.usuario_id] || '—');
      avisar('success', `PDF de la venta ${numeroVenta(venta.id)} generado correctamente.`);
    } catch {
      avisar('error', 'No se pudo generar el PDF de la venta. Intentá de nuevo.');
    } finally {
      setExportandoId(null);
    }
  };

  const handleDescargarHistorial = async () => {
    if (filtered.length === 0) {
      avisar('error', 'No hay ventas en el período seleccionado para exportar.');
      return;
    }
    setExportandoGlobal(true);
    try {
      await generarPdfHistorial(filtered, empleados, periodoLabel);
      avisar('success', `Historial exportado con ${filtered.length} venta(s).`);
    } catch {
      avisar('error', 'No se pudo generar el PDF del historial. Intentá de nuevo.');
    } finally {
      setExportandoGlobal(false);
    }
  };

  return (
    <Layout>
      <div className="fade-in space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-[#111827]">Historial de Ventas</h2>
            <p className="text-sm text-gray-500 mt-0.5">Registro completo de transacciones</p>
          </div>
          <button
            onClick={handleDescargarHistorial}
            disabled={exportandoGlobal || filtered.length === 0}
            className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1D4ED8] disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            {exportandoGlobal ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i> Generando historial...
              </>
            ) : (
              <>
                <i className="ri-file-pdf-2-line"></i> Descargar historial en PDF
              </>
            )}
          </button>
        </div>

        {error ? (
          <div className="bg-white rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-red-500 w-8 h-8 flex items-center justify-center"></i>
            </div>
            <h3 className="text-base font-semibold text-[#111827] mb-2">No pudimos cargar el historial</h3>
            <p className="text-sm text-gray-500 mb-4">{error}</p>
            <button
              onClick={cargar}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-[#2563EB] text-white text-sm font-medium hover:bg-[#1D4ED8] whitespace-nowrap cursor-pointer"
            >
              <i className="ri-refresh-line"></i> Reintentar
            </button>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="spinner w-10 h-10 border-3 border-[#2563EB] border-t-transparent mx-auto mb-4"></div>
              <p className="text-sm text-gray-500">Cargando historial...</p>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Ventas en el período</p>
                <p className="text-2xl font-bold text-[#111827]">{resumen.cantidad}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Ingresos por ventas</p>
                <p className="text-2xl font-bold text-[#2563EB] whitespace-nowrap">{formatearBs(resumen.ingresos)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Total recibido</p>
                <p className="text-2xl font-bold text-[#16A34A] whitespace-nowrap">{formatearBs(resumen.recibido)}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-gray-100">
                <p className="text-xs text-gray-500 mb-1">Total de cambios</p>
                <p className="text-2xl font-bold text-[#DC2626] whitespace-nowrap">{formatearBs(resumen.cambios)}</p>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4">
              <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 flex items-center justify-center"></i>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Buscar por cliente, NIT o N.º de venta..."
                    className="w-full pl-10 pr-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#2563EB]"
                  />
                </div>
                <select
                  value={filtroFecha}
                  onChange={(e) => setFiltroFecha(e.target.value as TipoFiltro)}
                  className="px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#2563EB] cursor-pointer"
                >
                  <option value="todas">Todas las fechas</option>
                  <option value="hoy">Hoy</option>
                  <option value="semana">Esta semana</option>
                  <option value="mes">Este mes</option>
                </select>
                <select
                  value={filtroPago}
                  onChange={(e) => setFiltroPago(e.target.value)}
                  className="px-4 py-2.5 bg-[#F4F6F8] border border-gray-200 rounded-lg text-sm text-gray-700 focus:outline-none focus:border-[#2563EB] cursor-pointer"
                >
                  <option value="">Todos los métodos</option>
                  <option value="efectivo">Efectivo</option>
                  <option value="qr">QR</option>
                  <option value="transferencia">Transferencia</option>
                </select>
              </div>
              <p className="text-xs text-gray-400 mt-3">
                Período: <span className="text-gray-600 font-medium">{periodoLabel}</span>
              </p>
            </div>

            {filtered.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center">
                <div className="w-20 h-20 bg-[#F4F6F8] rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <i className="ri-file-list-3-line text-3xl text-gray-400 w-10 h-10 flex items-center justify-center"></i>
                </div>
                <h3 className="text-lg font-semibold text-[#111827] mb-2">Sin resultados</h3>
                <p className="text-sm text-gray-500">
                  {ventas.length === 0
                    ? 'Aún no hay ventas registradas en el sistema.'
                    : 'No se encontraron ventas con los filtros actuales.'}
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-[#F4F6F8] border-b border-gray-100">
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Fecha y hora</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">N.º venta</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Cliente</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">NIT</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Método</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Recibido</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Cambio</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Total</th>
                        <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageItems.map((venta) => (
                        <tr key={venta.id} className="border-b border-gray-50 hover:bg-[#F4F6F8]/50 transition-colors">
                          <td className="px-4 py-3">
                            <p className="text-sm font-medium text-[#111827] whitespace-nowrap">
                              {new Date(venta.fecha).toLocaleDateString('es-BO')}
                            </p>
                            <p className="text-xs text-gray-400 whitespace-nowrap">
                              {new Date(venta.fecha).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs font-semibold text-[#2563EB] font-mono whitespace-nowrap">
                              {numeroVenta(venta.id)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                            {venta.nombre_factura || 'Consumidor Final'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 font-mono whitespace-nowrap">
                            {venta.nit || '—'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize whitespace-nowrap ${
                              venta.metodo_pago === 'efectivo'
                                ? 'bg-[#16A34A]/10 text-[#16A34A]'
                                : venta.metodo_pago === 'qr'
                                ? 'bg-[#2563EB]/10 text-[#2563EB]'
                                : 'bg-amber-100 text-amber-600'
                            }`}>
                              {venta.metodo_pago}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-right whitespace-nowrap">
                            {formatearBs(Number(venta.monto_recibido))}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 text-right whitespace-nowrap">
                            {formatearBs(Number(venta.cambio))}
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-[#111827] text-right whitespace-nowrap">
                            {formatearBs(Number(venta.total))}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => setDetalle(venta)}
                                title="Ver venta"
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-[#2563EB] hover:bg-[#2563EB]/10 cursor-pointer"
                              >
                                <i className="ri-eye-line text-base"></i>
                              </button>
                              <button
                                onClick={() => handleDescargarVenta(venta)}
                                disabled={exportandoId === venta.id}
                                title="Descargar PDF"
                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-[#DC2626] hover:bg-[#DC2626]/10 disabled:opacity-50 cursor-pointer"
                              >
                                {exportandoId === venta.id ? (
                                  <i className="ri-loader-4-line animate-spin text-base"></i>
                                ) : (
                                  <i className="ri-file-pdf-2-line text-base"></i>
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="px-4 py-3 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <p className="text-sm text-gray-500">
                    {filtered.length} venta{filtered.length !== 1 ? 's' : ''} encontrada{filtered.length !== 1 ? 's' : ''}
                    {' · '}Total:{' '}
                    <strong className="text-[#111827]">{formatearBs(resumen.ingresos)}</strong>
                  </p>
                  {totalPaginas > 1 && (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setPagina((p) => Math.max(1, p - 1))}
                        disabled={paginaActual === 1}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                      >
                        <i className="ri-arrow-left-s-line text-lg"></i>
                      </button>
                      <span className="text-sm text-gray-600 whitespace-nowrap">
                        Página {paginaActual} de {totalPaginas}
                      </span>
                      <button
                        onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                        disabled={paginaActual === totalPaginas}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
                      >
                        <i className="ri-arrow-right-s-line text-lg"></i>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {detalle && (
        <DetalleVentaModal
          venta={detalle}
          empleado={empleados[detalle.usuario_id] || '—'}
          onClose={() => setDetalle(null)}
          onDescargar={() => handleDescargarVenta(detalle)}
          exportando={exportandoId === detalle.id}
        />
      )}

      {notificacion && (
        <div className="fixed bottom-5 right-5 z-[60]">
          <div
            className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border ${
              notificacion.tipo === 'success'
                ? 'bg-white border-green-200'
                : 'bg-white border-red-200'
            }`}
          >
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 ${
                notificacion.tipo === 'success' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
              }`}
            >
              <i className={notificacion.tipo === 'success' ? 'ri-check-line' : 'ri-error-warning-line'}></i>
            </div>
            <p className="text-sm text-[#111827]">{notificacion.mensaje}</p>
          </div>
        </div>
      )}
    </Layout>
  );
}